import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { HlmBtnDirective, HlmIconComponent } from '../../ui';

/**
 * Inline error state — alert card with icon, title, message, optional retry.
 *
 *   <cmt-error-state [message]="error()" (retry)="loadData()" />
 */
@Component({
  selector: 'cmt-error-state',
  standalone: true,
  imports: [HlmIconComponent, HlmBtnDirective],
  template: `
    <div
      role="alert"
      class="relative flex items-start gap-4 overflow-hidden rounded-2xl border border-destructive/25 bg-card p-5 shadow-sm"
    >
      <span
        aria-hidden="true"
        class="absolute inset-y-0 left-0 w-1 bg-destructive/60"
      ></span>
      <span
        aria-hidden="true"
        class="grid h-10 w-10 flex-none place-items-center rounded-xl bg-destructive/10 text-destructive"
      >
        <hlm-icon name="lucideCircleAlert" size="1.25rem" />
      </span>
      <div class="min-w-0 flex-1">
        <h3 class="m-0 text-sm font-semibold text-foreground">{{ title() }}</h3>
        @if (message()) {
          <p class="mt-1 text-sm text-foreground/80">{{ message() }}</p>
        }
        @if (hint()) {
          <p class="mt-1 text-sm text-muted-foreground">{{ hint() }}</p>
        }
      </div>
      @if (showRetry()) {
        <button hlmBtn variant="outline" size="sm" (click)="retry.emit()">
          <hlm-icon name="lucideRefreshCw" size="0.85rem" />
          {{ retryLabel() }}
        </button>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorStateComponent {
  title = input<string>('Something went wrong');
  message = input<string>('');
  hint = input<string>('');
  showRetry = input<boolean>(true);
  retryLabel = input<string>('Retry');
  retry = output<void>();
}
